import { useEffect, useMemo, useRef, useState } from "react";
import { NewReleases } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
    JIGSAW_MUSE_GOOGLE_PLAY_URL,
    SHOW_JIGSAW_GOOGLE_PLAY_BADGE,
} from "../../../config/nativeAppsVisibility";
import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import UserService from "../../../services/UserService";
import UtilService from "../../../services/UtilService";
import TemplateLevelGroup, { JIGSAW_PLAY_WARM } from "./TemplateLevelGroup";
import IconBack from "../../components/IconBack";
import AppStorePromoPill from "../../components/AppStorePromoPill";
import GooglePlayBadgeLink from "../../components/GooglePlayBadgeLink";
import SubscriptionDialog from "../../subscription/SubscriptionDialog";
import { usePagesTranslation } from "../../locales";
import { TagChipAvatar } from "../common/components/TagChipAvatar";
import { buildPuzzleAccessContext } from "../common/puzzleTierAccess";
import { isJigsawGroupDiscarded, isJigsawGroupUnavailable, normalizeHomeJigsawGroup } from "./JigsawService";
import {
    buildJigsawPlayLevelEntries,
    mergeJigsawPlayTemplates,
    pickDefaultJigsawPlayTemplate,
    pickJigsawTemplateForTileSize,
    pickPrimaryJigsawSeed,
    resolveJigsawTemplateTileSize,
} from "./jigsawBrowseUtils";
import {
    resolveJigsawPlayTemplate,
    saveJigsawPrefsFromTemplate,
} from "../common/puzzlePlayPrefs";
import { trackEvent } from "../../../services/GoogleAnalytics";
import SlidingPlayMistBackground from "../sliding/SlidingPlayMistBackground";
import { SITE_BG } from "../../../dark-theme/palette";
import LandingColorBasketCard from "../../login/LandingColorBasketCard";
import { loadColorBasketsForJigsaw } from "./JigsawColorBasketPlayService";

const JIGSAW_MUSE_APP_ICON = `${process.env.PUBLIC_URL || ""}/ad/jigsaw-muse-app-icon-512.png`;

/** Header text sits straight on the night mist, so it runs light. */
const MIST_HEADING_COLOR = "#eaf6ef";
const MIST_BODY_COLOR = "rgba(226,232,240,0.82)";
/** Copy inside the frosted white panels — dark theme `text.*` is unreadable there. */
const PANEL_INK = "#163028";
const PANEL_MUTED = "rgba(22, 48, 40, 0.78)";

const PANEL_PAPER_SX = {
    p: { xs: 1.5, sm: 2 },
    bgcolor: "rgba(255,255,255,0.78)",
    backdropFilter: "blur(10px)",
    borderColor: "rgba(255,255,255,0.55)",
    boxShadow: "0 8px 28px rgba(40, 70, 55, 0.12)",
    color: PANEL_INK,
};

const PLAY_BUTTON_SX = {
    background: `linear-gradient(165deg, #E0A85A 0%, ${JIGSAW_PLAY_WARM.main} 48%, ${JIGSAW_PLAY_WARM.dark} 100%)`,
    color: "#1a1208",
    px: 4,
    minWidth: 140,
    minHeight: 44,
    textTransform: "none",
    fontWeight: 700,
    letterSpacing: "0.04em",
    boxShadow: `0 4px 14px ${JIGSAW_PLAY_WARM.soft}, inset 0 1px 0 rgba(255,255,255,0.28)`,
    transition: "transform 160ms ease, box-shadow 160ms ease, filter 160ms ease",
    "&:hover": {
        background: `linear-gradient(165deg, #E8B56A 0%, #D49644 48%, ${JIGSAW_PLAY_WARM.dark} 100%)`,
        boxShadow: `0 8px 22px ${JIGSAW_PLAY_WARM.glow}, inset 0 1px 0 rgba(255,255,255,0.35)`,
        filter: "brightness(1.04)",
        transform: "translateY(-1px)",
    },
    "&:active": {
        transform: "translateY(0)",
        boxShadow: `0 2px 10px ${JIGSAW_PLAY_WARM.soft}`,
    },
};

function isPublishedWithinDays(publishedAt, days = 1) {
    if (publishedAt == null) return false;
    const pubMs = new Date(publishedAt).getTime();
    if (!Number.isFinite(pubMs) || pubMs <= 0) return false;
    const now = Date.now();
    if (pubMs > now) return false;
    return pubMs >= now - days * 24 * 60 * 60 * 1000;
}
function getStoryTitle(story) {
    if (!story) return "";
    return story.english?.title ?? story.en?.title ?? story.french?.title ?? story.fr?.title ?? "";
}

function getImageUrl(story) {
    if (!story) return null;
    return story.imageUrl ?? story.imagePreviewUrl ?? story.imageIcon400Url ?? null;
}

export default function JigsawPlayPage() {
    const { storyId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = usePagesTranslation();
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [difficultyDialogOpen, setDifficultyDialogOpen] = useState(false);
    const [basketsByJigsawId, setBasketsByJigsawId] = useState({});
    const prefsAppliedStoryRef = useRef(null);

    const cloudUserId = userInfo?.cloudUser?.id ?? null;

    useEffect(() => {
        let cancelled = false;
        UserService.getUserInfo()
            .then((data) => {
                if (!cancelled) setUserInfo(data);
            })
            .catch(() => {
                if (!cancelled) setUserInfo(null);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const accessContext = useMemo(() => buildPuzzleAccessContext(userInfo), [userInfo]);
    const userPayPlan = UtilService.resolveWebPayPlan(userInfo?.cloudUser);

    const handleBack = () => {
        if (location.state?.fromLibrary === true) {
            navigate("/account/library", {
                state: { activeTab: location.state?.activeTab ?? 0 },
            });
            return;
        }
        navigate("/puzzles/jigsaw");
    };

    const playRecordPuzzleId = selectedTemplate?.id ? String(selectedTemplate.id).trim() : null;

    useEffect(() => {
        trackEvent("entered", {
            name: "JigsawPlayPage",
            puzzle_type: "JIGSAW",
            page_kind: "select",
            story_id: storyId ?? undefined,
            puzzle_id: playRecordPuzzleId ?? undefined,
        });
    }, [storyId, playRecordPuzzleId]);

    useEffect(() => {
        if (!storyId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await ApiService.getData(`/home/jigsaw/${encodeURIComponent(storyId)}`);
                const normalized = normalizeHomeJigsawGroup(data) ?? data;
                if (!cancelled) {
                    setGroup(normalized);
                }
            } catch (e) {
                Logger.error("JigsawPlayPage:", e);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [storyId]);

    const story = group?.story ?? null;
    const storyJigsaw = group?.storyJigsaw ?? null;
    const isUnavailable = isJigsawGroupUnavailable(group);
    const isDiscarded = isJigsawGroupDiscarded(group);
    const title = getStoryTitle(story) || group?.puzzleTemplate?.title || "";
    const puzzleTags = (group?.puzzleTemplate?.tags || [])
        .filter((tag) => tag.toLowerCase() !== "bedrock")
        .map((tag) => String(tag).trim().toLowerCase());
    const imageUrl = getImageUrl(story);
    const templates = group?.templates ?? [];
    const userPlays = group?.userPlays ?? [];
    const playTemplates = useMemo(
        () => mergeJigsawPlayTemplates(templates, userPlays),
        [templates, userPlays],
    );
    const playLevelEntries = useMemo(
        () => buildJigsawPlayLevelEntries(playTemplates),
        [playTemplates],
    );
    const hasColorBasketLevels = useMemo(
        () => playTemplates.some((tpl) => resolveJigsawTemplateTileSize(tpl) >= 9),
        [playTemplates],
    );

    useEffect(() => {
        const eligible = playTemplates.filter(
            (tpl) => tpl?.id && resolveJigsawTemplateTileSize(tpl) >= 9,
        );
        if (eligible.length === 0) {
            setBasketsByJigsawId({});
            return undefined;
        }
        let cancelled = false;
        (async () => {
            const entries = await Promise.all(
                eligible.map(async (tpl) => {
                    const id = String(tpl.id);
                    try {
                        const baskets = await loadColorBasketsForJigsaw(id);
                        return [id, baskets];
                    } catch (e) {
                        Logger.error("JigsawPlayPage color baskets:", e);
                        return [id, []];
                    }
                }),
            );
            if (cancelled) return;
            const map = {};
            for (const [id, baskets] of entries) {
                map[id] = baskets;
            }
            setBasketsByJigsawId(map);
        })();
        return () => {
            cancelled = true;
        };
    }, [playTemplates]);
    const storyPublishedAt =
        storyJigsaw?.publishedAt ??
        group?.puzzleTemplate?.publishedAt ??
        story?.publishedAt ??
        null;
    const isStoryNew = useMemo(
        () => isPublishedWithinDays(storyPublishedAt),
        [storyPublishedAt],
    );

    useEffect(() => {
        prefsAppliedStoryRef.current = null;
    }, [storyId]);

    useEffect(() => {
        if (!storyId || playTemplates.length === 0) return;
        const appliedKey = `${storyId}:${cloudUserId ?? "guest"}`;
        if (prefsAppliedStoryRef.current === appliedKey) return;
        prefsAppliedStoryRef.current = appliedKey;
        const tpl = cloudUserId
            ? resolveJigsawPlayTemplate(playTemplates, cloudUserId)
            : pickDefaultJigsawPlayTemplate(playTemplates);
        if (tpl) setSelectedTemplate(tpl);
    }, [storyId, playTemplates, cloudUserId]);

    useEffect(() => {
        if (!cloudUserId || !selectedTemplate) return;
        saveJigsawPrefsFromTemplate(cloudUserId, selectedTemplate);
    }, [cloudUserId, selectedTemplate]);

    return (
        <Box
            sx={{
                position: "relative",
                isolation: "isolate",
                width: "100%",
                flex: 1,
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
                overflowX: "hidden",
                overflowY: "auto",
                bgcolor: SITE_BG,
            }}
        >
            <SlidingPlayMistBackground variant="night" />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    p: { xs: 2, sm: 3 },
                    maxWidth: 1100,
                    width: "100%",
                    mx: "auto",
                    boxSizing: "border-box",
                }}
            >
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !group ? (
                <Typography sx={{ color: MIST_BODY_COLOR }}>Puzzle not found.</Typography>
            ) : (
                <>
                    <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                        <IconBack onClick={handleBack} sx={{ alignSelf: "flex-start", mt: 0.5 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                                {isStoryNew && !isUnavailable ? (
                                    <Chip
                                        icon={<NewReleases />}
                                        label={t("puzzle.card.new")}
                                        size="small"
                                        color="error"
                                        variant="filled"
                                        sx={{
                                            backgroundColor: "#ff4444",
                                            color: "white",
                                            fontWeight: 700,
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                                            flexShrink: 0,
                                        }}
                                    />
                                ) : null}
                                <Typography variant="h6" sx={{ color: MIST_HEADING_COLOR, fontWeight: 600 }}>
                                    {title || "Jigsaw Puzzle"}
                                </Typography>
                                {isUnavailable ? (
                                    <Chip
                                        size="small"
                                        label={t("puzzle.jigsaw.not-available", "Not available")}
                                        sx={{
                                            bgcolor: "grey.500",
                                            color: "common.white",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                        }}
                                    />
                                ) : null}
                                {puzzleTags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        avatar={<TagChipAvatar tag={tag} size={18} />}
                                        label={tag}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            fontSize: "0.7rem",
                                            height: 20,
                                            bgcolor: "rgba(255,255,255,0.55)",
                                        }}
                                    />
                                ))}
                            </Box>
                            <Typography variant="body2" sx={{ color: MIST_BODY_COLOR }}>
                                {isUnavailable
                                    ? isDiscarded
                                        ? t(
                                              "puzzle.jigsaw.unavailable-discarded",
                                              "This puzzle is no longer available.",
                                          )
                                        : t(
                                              "puzzle.jigsaw.will-be-removed-in-week",
                                              "Will be removed in 1 week",
                                          )
                                    : t(
                                          "puzzle.jigsaw.tap-play-to-choose",
                                          "Tap Play to choose a difficulty and start.",
                                      )}
                            </Typography>
                        </Box>
                    </Box>

                    {(playTemplates.length > 0 || isUnavailable) && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2.5,
                                width: "100%",
                                maxWidth: 720,
                                mx: "auto",
                            }}
                        >
                            <Paper variant="outlined" sx={{ ...PANEL_PAPER_SX, width: "100%" }}>
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        bgcolor: "grey.900",
                                    }}
                                >
                                    {imageUrl ? (
                                        <Box
                                            component="img"
                                            src={imageUrl}
                                            alt={title || ""}
                                            referrerPolicy="no-referrer"
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                                display: "block",
                                                filter: isUnavailable ? "grayscale(1)" : "none",
                                                opacity: isUnavailable ? 0.72 : 1,
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ color: PANEL_MUTED }}>
                                                —
                                            </Typography>
                                        </Box>
                                    )}
                                    {isUnavailable ? (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "rgba(50,50,50,0.55)",
                                                px: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{ color: "common.white", fontWeight: 700, textAlign: "center" }}
                                            >
                                                {t("puzzle.jigsaw.not-available", "Not available")}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "common.white", textAlign: "center", mt: 0.5 }}
                                            >
                                                {isDiscarded
                                                    ? t(
                                                          "puzzle.jigsaw.unavailable-discarded",
                                                          "This puzzle is no longer available.",
                                                      )
                                                    : t(
                                                          "puzzle.jigsaw.will-be-removed-in-week",
                                                          "Will be removed in 1 week",
                                                      )}
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Paper>

                            {isUnavailable ? (
                                <Alert
                                    severity={isDiscarded ? "info" : "warning"}
                                    sx={{ width: "100%" }}
                                >
                                    {isDiscarded
                                        ? t(
                                              "puzzle.jigsaw.unavailable-discarded",
                                              "This puzzle is no longer available.",
                                          )
                                        : t(
                                              "puzzle.jigsaw.will-be-removed-in-week",
                                              "Will be removed in 1 week",
                                          )}
                                </Alert>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        const seed = pickPrimaryJigsawSeed(playTemplates);
                                        const medium = pickJigsawTemplateForTileSize(
                                            playTemplates,
                                            9,
                                            seed,
                                        );
                                        setSelectedTemplate(
                                            medium || pickDefaultJigsawPlayTemplate(playTemplates),
                                        );
                                        setDifficultyDialogOpen(true);
                                    }}
                                    sx={{
                                        ...PLAY_BUTTON_SX,
                                        visibility: difficultyDialogOpen ? "hidden" : "visible",
                                    }}
                                >
                                    {t("puzzle.card.play")}
                                </Button>
                            )}
                        </Box>
                    )}

                    <Dialog
                        open={difficultyDialogOpen && !isUnavailable}
                        onClose={() => setDifficultyDialogOpen(false)}
                        maxWidth="sm"
                        fullWidth
                        aria-labelledby="jigsaw-difficulty-dialog-title"
                    >
                        <DialogTitle
                            id="jigsaw-difficulty-dialog-title"
                            sx={{
                                pr: 6,
                                fontWeight: 700,
                            }}
                        >
                            {t("puzzle.jigsaw.select-difficulty")}
                            <IconButton
                                aria-label={t("common.close", "Close")}
                                onClick={() => setDifficultyDialogOpen(false)}
                                sx={{
                                    position: "absolute",
                                    right: 8,
                                    top: 8,
                                    minWidth: 44,
                                    minHeight: 44,
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t(
                                    "puzzle.jigsaw.difficulty-dialog-hint",
                                    "Medium is selected by default. Choose a level, then tap Play.",
                                )}
                            </Typography>
                            <TemplateLevelGroup
                                levelEntries={playLevelEntries}
                                selectedTemplate={selectedTemplate}
                                setSelectedTemplate={setSelectedTemplate}
                                storyJigsaw={storyJigsaw}
                                userPlays={userPlays}
                                accessContext={accessContext}
                                onUpgrade={() => {
                                    setDifficultyDialogOpen(false);
                                    setSubscriptionModalOpen(true);
                                }}
                                navigationState={location.state}
                                basketsByJigsawId={basketsByJigsawId}
                            />
                        </DialogContent>
                    </Dialog>

                    {hasColorBasketLevels && !isUnavailable ? (
                        <Box sx={{ mt: 3 }}>
                            <LandingColorBasketCard
                                embedded
                                showBrowseCta={false}
                                showToolbarHint
                                title={t(
                                    "puzzle.jigsaw.color-baskets-guide-title",
                                    "Color baskets",
                                )}
                                body={t(
                                    "puzzle.jigsaw.color-baskets-guide-body",
                                    "Color baskets are available on Medium through Insane difficulties (9×9 and larger). Group pieces by color, assemble one basket at a time, then Deploy onto the main board. Here's how:",
                                )}
                            />
                        </Box>
                    ) : null}

                    {SHOW_JIGSAW_GOOGLE_PLAY_BADGE ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 3,
                                mb: 1,
                            }}
                        >
                            <AppStorePromoPill
                                iconSrc={JIGSAW_MUSE_APP_ICON}
                                appName="Jigsaw Muse"
                                tagline={t(
                                    "marketing.jigsaw-muse-promo",
                                    "Classic photo jigsaws with Color Baskets.",
                                )}
                            >
                                <GooglePlayBadgeLink
                                    height={36}
                                    href={JIGSAW_MUSE_GOOGLE_PLAY_URL}
                                    ariaLabel={t(
                                        "landing.jigsaw-google-play-badge-aria",
                                        "Get Jigsaw Muse on Google Play (opens in a new tab)",
                                    )}
                                />
                            </AppStorePromoPill>
                        </Box>
                    ) : null}
                </>
            )}

            <SubscriptionDialog
                open={subscriptionModalOpen}
                onClose={() => setSubscriptionModalOpen(false)}
                onSubscribe={() => {
                    setSubscriptionModalOpen(false);
                    window.location.reload();
                }}
                triggerReason="puzzle"
                userSubscription={userPayPlan || "free"}
            />
            </Box>
        </Box>
    );
}
